<template>
  <div ref="roadContainer" class="road-map-container relative overflow-y-auto overflow-x-hidden">
    <!-- Full-width background -->
    <div class="road-bg absolute inset-0 pointer-events-none" />

    <div
      class="road-map-inner relative"
      :style="{ height: totalHeight + 'px' }"
    >

      <!-- Title banner -->
      <div class="absolute left-1/2 -translate-x-1/2 z-10" :style="{ top: '12px' }">
        <div class="flex items-center gap-2 px-5 py-2 rounded-xl bg-white/90 shadow-md border border-yellow-300">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" fill="#fbbf24" />
            <text x="12" y="16" text-anchor="middle" font-size="12" font-weight="bold" fill="#78350f">&#9733;</text>
          </svg>
          <span class="text-lg font-bold text-amber-800">Road Map</span>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="11" fill="#fbbf24" />
            <text x="12" y="16" text-anchor="middle" font-size="12" font-weight="bold" fill="#78350f">&#9733;</text>
          </svg>
        </div>
      </div>

      <!-- SVG layer: road, decorations, animals -->
      <svg
        class="absolute inset-0 w-full h-full pointer-events-none"
        :viewBox="`0 0 ${containerWidth} ${totalHeight}`"
      >
        <!-- Grass patches -->
        <template v-for="grass in grassPatches" :key="'grass-' + grass.x + grass.y">
          <ellipse :cx="grass.x" :cy="grass.y" :rx="grass.rx" :ry="grass.ry"
            :fill="grass.color" :opacity="grass.opacity" />
        </template>

        <!-- Road shadow -->
        <path
          v-if="roadPath"
          :d="roadPath"
          stroke="#6b7280"
          :stroke-width="roadStrokeWidth + 6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.15"
        />

        <!-- Road surface (asphalt) -->
        <path
          v-if="roadPath"
          :d="roadPath"
          stroke="#6b7280"
          :stroke-width="roadStrokeWidth"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <!-- Road center dashed line -->
        <path
          v-if="roadPath"
          :d="roadPath"
          stroke="white"
          :stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-dasharray="12 8"
          opacity="0.8"
        />

        <!-- Road edge markings -->
        <path
          v-if="roadPath"
          :d="roadPath"
          stroke="white"
          :stroke-width="roadStrokeWidth + 2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.2"
        />

        <!-- Completed road portion (yellow/gold overlay) -->
        <path
          v-if="completedPath"
          :d="completedPath"
          stroke="#fbbf24"
          :stroke-width="roadStrokeWidth - 4"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.35"
          class="road-completed-line"
          :style="{ strokeDasharray: completedPathLength, strokeDashoffset: animatedOffset }"
        />

        <!-- Scattered animals / fun characters -->
        <template v-for="animal in animals" :key="'animal-' + animal.x + animal.y">
          <g :transform="`translate(${animal.x}, ${animal.y}) scale(0.7)`">
            <!-- Moose/reindeer like reference image -->
            <template v-if="animal.type === 'moose'">
              <ellipse cx="0" cy="4" rx="8" ry="10" fill="#92400e" /> <!-- body -->
              <circle cx="0" cy="-8" r="5" fill="#a16207" /> <!-- head -->
              <circle cx="-2" cy="-9" r="1" fill="#1e293b" /> <!-- eye -->
              <circle cx="2" cy="-9" r="1" fill="#1e293b" /> <!-- eye -->
              <!-- Antlers -->
              <path d="M-4,-12 L-8,-20 M-6,-16 L-10,-17" stroke="#78350f" stroke-width="1.5" fill="none" />
              <path d="M4,-12 L8,-20 M6,-16 L10,-17" stroke="#78350f" stroke-width="1.5" fill="none" />
              <!-- Legs -->
              <line x1="-4" y1="14" x2="-4" y2="22" stroke="#78350f" stroke-width="2" />
              <line x1="4" y1="14" x2="4" y2="22" stroke="#78350f" stroke-width="2" />
            </template>
            <!-- Bird -->
            <template v-else-if="animal.type === 'bird'">
              <path d="M-6,0 Q0,-6 6,0" stroke="#3b82f6" stroke-width="2" fill="none" />
              <path d="M-3,0 Q0,-4 3,0" stroke="#60a5fa" stroke-width="1.5" fill="none" />
            </template>
            <!-- Star -->
            <template v-else>
              <polygon points="0,-8 2,-3 7,-3 3,1 5,6 0,3 -5,6 -3,1 -7,-3 -2,-3"
                :fill="animal.color" opacity="0.8" />
            </template>
          </g>
        </template>

        <!-- Trees alongside the road -->
        <template v-for="tree in trees" :key="'tree-' + tree.x + tree.y">
          <g :transform="`translate(${tree.x}, ${tree.y}) scale(${tree.scale})`">
            <!-- Round tree (different from mountain triangular trees) -->
            <rect x="-2" y="0" width="4" height="10" fill="#92400e" rx="1.5" />
            <circle cx="0" cy="-4" r="10" :fill="tree.color" opacity="0.7" />
            <circle cx="-5" cy="-2" r="7" :fill="tree.color" opacity="0.5" />
            <circle cx="5" cy="-2" r="7" :fill="tree.color" opacity="0.5" />
          </g>
        </template>
      </svg>

      <!-- Signpost markers at unit boundaries -->
      <RoadMapSignpost
        v-for="sign in signpostPositions"
        :key="'sign-' + sign.unitId"
        :title="sign.title"
        :style="{
          position: 'absolute',
          left: sign.xPercent + '%',
          transform: 'translateX(-50%)',
          top: sign.y + 'px'
        }"
      />

      <!-- Lesson nodes -->
      <div
        v-for="(node, idx) in nodePositions"
        :key="node.lesson.id"
        :style="{
          position: 'absolute',
          left: node.xPercent + '%',
          top: node.y + 'px',
          transform: 'translate(-50%, -50%)'
        }"
      >
        <RoadMapNode
          :lesson="node.lesson"
          :index="idx"
          :side="node.side"
          @select="$emit('select-lesson', $event)"
        />
      </div>

      <!-- Car avatar at current lesson -->
      <RoadMapCar
        v-if="currentNodePosition"
        :x="currentNodePosition.xPx"
        :y="currentNodePosition.y"
      />

      <!-- START line at bottom -->
      <div
        class="absolute -translate-x-1/2"
        :style="{ left: firstNodePercent + '%', top: (firstNodeY + 40) + 'px' }"
      >
        <div class="flex flex-col items-center">
          <!-- Checkered flag pattern -->
          <svg width="120" height="24" viewBox="0 0 120 24" fill="none">
            <rect x="0" y="0" width="120" height="24" rx="4" fill="#1e293b" />
            <!-- Checkered pattern -->
            <template v-for="row in 2" :key="'cf-row-' + row">
              <template v-for="col in 10" :key="'cf-' + row + '-' + col">
                <rect
                  v-if="(row + col) % 2 === 0"
                  :x="(col - 1) * 12" :y="(row - 1) * 12"
                  width="12" height="12" fill="white"
                />
              </template>
            </template>
          </svg>
          <div class="px-4 py-1 rounded-full bg-yellow-400 text-yellow-900 text-xs font-bold mt-1 shadow-sm">
            START
          </div>
        </div>
      </div>

      <!-- FINISH banner at top -->
      <div
        class="absolute -translate-x-1/2"
        :style="{ left: lastNodePercent + '%', top: (lastNodeY - 50) + 'px' }"
      >
        <div class="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <polygon points="10,0 13,7 20,7 14,12 16,20 10,15 4,20 6,12 0,7 7,7" fill="#fbbf24" />
          </svg>
          <span class="text-xs font-bold text-amber-700 bg-amber-100 px-3 py-1 rounded-full">FINISH</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <polygon points="10,0 13,7 20,7 14,12 16,20 10,15 4,20 6,12 0,7 7,7" fill="#fbbf24" />
          </svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick, onUnmounted } from 'vue';
import RoadMapNode from './RoadMapNode.vue';
import RoadMapSignpost from './RoadMapSignpost.vue';
import RoadMapCar from './RoadMapCar.vue';

const props = defineProps({
  units: { type: Array, required: true }
});

defineEmits(['select-lesson']);

const roadContainer = ref(null);
const animatedOffset = ref('9999');
const containerWidth = ref(800);

// Responsive dimensions as percentages
const leftPercent = ref(20);
const rightPercent = ref(80);
const roadStrokeWidth = ref(28);

function updateDimensions() {
  const w = window.innerWidth;
  containerWidth.value = w;
  if (w < 640) {
    leftPercent.value = 18;
    rightPercent.value = 82;
    roadStrokeWidth.value = 22;
  } else if (w < 1024) {
    leftPercent.value = 22;
    rightPercent.value = 78;
    roadStrokeWidth.value = 26;
  } else {
    leftPercent.value = 25;
    rightPercent.value = 75;
    roadStrokeWidth.value = 30;
  }
}

const NODE_SPACING_Y = 100;
const SIGNPOST_HEIGHT = 70;
const TOP_PADDING = 100;
const BOTTOM_PADDING = 70;

// Node positions: snake back and forth
const nodePositions = computed(() => {
  const positions = [];
  let globalIndex = 0;

  for (const unit of props.units) {
    for (let i = 0; i < unit.lessons.length; i++) {
      const GROUP_SIZE = 3;
      const groupIndex = Math.floor(globalIndex / GROUP_SIZE);
      const posInGroup = globalIndex % GROUP_SIZE;
      const isLeft = groupIndex % 2 === 0;
      const basePct = isLeft ? leftPercent.value : rightPercent.value;
      const spreadDir = isLeft ? 1 : -1;
      const pct = basePct + posInGroup * 4 * spreadDir;
      positions.push({
        lesson: unit.lessons[i],
        unitId: unit.id,
        globalIndex,
        xPercent: pct,
        xPx: (pct / 100) * containerWidth.value,
        side: isLeft ? 'right' : 'left', // arrow points toward center
        isFirstInUnit: i === 0,
        isLastInUnit: i === unit.lessons.length - 1
      });
      globalIndex++;
    }
  }

  // Total height, invert Y (bottom = start)
  const unitCount = props.units.length;
  const rawTotalHeight = positions.length * NODE_SPACING_Y + unitCount * SIGNPOST_HEIGHT + TOP_PADDING + BOTTOM_PADDING;

  let currentY = rawTotalHeight - BOTTOM_PADDING;
  let unitIdx = 0;

  for (let i = 0; i < positions.length; i++) {
    positions[i].y = currentY;
    currentY -= NODE_SPACING_Y;

    if (positions[i].isLastInUnit && unitIdx < unitCount - 1) {
      currentY -= SIGNPOST_HEIGHT;
      unitIdx++;
    }
  }

  return positions;
});

// Signpost positions between units
const signpostPositions = computed(() => {
  const signs = [];
  for (let i = 0; i < nodePositions.value.length; i++) {
    const node = nodePositions.value[i];
    if (node.isLastInUnit && i < nodePositions.value.length - 1) {
      const nextNode = nodePositions.value[i + 1];
      signs.push({
        unitId: nextNode.unitId,
        title: props.units.find(u => u.id === nextNode.unitId)?.title || '',
        y: (node.y + nextNode.y) / 2 - 20,
        xPercent: (node.xPercent + nextNode.xPercent) / 2
      });
    }
  }
  return signs;
});

const totalHeight = computed(() => {
  if (!nodePositions.value.length) return 400;
  const unitCount = props.units.length;
  return nodePositions.value.length * NODE_SPACING_Y + unitCount * SIGNPOST_HEIGHT + TOP_PADDING + BOTTOM_PADDING;
});

const firstNodePercent = computed(() => nodePositions.value[0]?.xPercent || 50);
const firstNodeY = computed(() => nodePositions.value[0]?.y || totalHeight.value - BOTTOM_PADDING);
const lastNodePercent = computed(() => nodePositions.value[nodePositions.value.length - 1]?.xPercent || 50);
const lastNodeY = computed(() => nodePositions.value[nodePositions.value.length - 1]?.y || TOP_PADDING);

const currentNodePosition = computed(() => {
  return nodePositions.value.find(n => n.lesson.status === 'current') || null;
});

// The road center-line path connects all node positions with smooth S-curves
const roadCenterPositions = computed(() => {
  const w = containerWidth.value;
  return nodePositions.value.map(n => ({
    // Shift toward center from node position
    x: n.xPx < w / 2
      ? n.xPx + 40
      : n.xPx - 40,
    y: n.y
  }));
});

const roadPath = computed(() => {
  const pts = roadCenterPositions.value;
  if (pts.length < 2) return '';

  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const midY = (prev.y + curr.y) / 2;
    d += ` C ${prev.x} ${midY}, ${curr.x} ${midY}, ${curr.x} ${curr.y}`;
  }
  return d;
});

const completedPath = computed(() => {
  const pts = roadCenterPositions.value;
  const nodes = nodePositions.value;
  // Extend solid line to include the current lesson (not just completed)
  const lastActive = nodes.reduce((last, n, i) => (n.lesson.status === 'completed' || n.lesson.status === 'current') ? i : last, -1);
  if (lastActive < 0) return '';

  const end = lastActive + 1;
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < end; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const midY = (prev.y + curr.y) / 2;
    d += ` C ${prev.x} ${midY}, ${curr.x} ${midY}, ${curr.x} ${curr.y}`;
  }
  return d;
});

const completedPathLength = computed(() => {
  const pts = roadCenterPositions.value;
  const nodes = nodePositions.value;
  const lastActive = nodes.reduce((last, n, i) => (n.lesson.status === 'completed' || n.lesson.status === 'current') ? i : last, -1);
  if (lastActive < 1) return '0';
  let len = 0;
  for (let i = 1; i <= lastActive; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    len += Math.sqrt(dx * dx + dy * dy) * 1.3;
  }
  return String(Math.round(len));
});

// Grass patches scattered in background
const grassPatches = computed(() => {
  const result = [];
  const h = totalHeight.value;
  const w = containerWidth.value;
  for (let i = 0; i < 20; i++) {
    const seed = i * 13 + 7;
    result.push({
      x: (seed * 37) % w,
      y: (seed * 53) % h,
      rx: 20 + (seed % 30),
      ry: 10 + (seed % 15),
      color: ['#86efac', '#bbf7d0', '#dcfce7'][(seed) % 3],
      opacity: 0.3 + (seed % 3) * 0.1
    });
  }
  return result;
});

// Trees alongside the road
const trees = computed(() => {
  const result = [];
  const nodes = nodePositions.value;
  const w = containerWidth.value;

  for (let i = 0; i < nodes.length; i++) {
    const seed = i * 11 + 5;
    if (i % 3 !== 0) continue;

    const side = nodes[i].xPx < w / 2 ? 'right' : 'left';
    const baseX = side === 'right' ? w * 0.9 - (seed % 40) : w * 0.1 + (seed % 40);

    result.push({
      x: baseX,
      y: nodes[i].y + (seed % 30) - 15,
      scale: 0.5 + (seed % 3) * 0.15,
      color: ['#16a34a', '#22c55e', '#4ade80'][seed % 3]
    });
  }
  return result;
});

// Fun animal characters scattered around
const animals = computed(() => {
  const result = [];
  const nodes = nodePositions.value;
  const w = containerWidth.value;

  for (let i = 0; i < nodes.length; i++) {
    const seed = i * 17 + 3;
    if (i % 4 !== 1) continue;

    const side = nodes[i].xPx < w / 2 ? 'right' : 'left';
    const baseX = side === 'right' ? w * 0.92 - (seed % 30) : w * 0.08 + (seed % 30);

    const types = ['moose', 'moose', 'bird', 'star'];
    const colors = ['#fbbf24', '#f59e0b', '#fcd34d'];

    result.push({
      x: baseX,
      y: nodes[i].y - 10 + (seed % 20),
      type: types[seed % types.length],
      color: colors[seed % colors.length]
    });
  }

  // Always add some stars at the top
  result.push({ x: w * 0.92, y: 30, type: 'star', color: '#fbbf24' });
  result.push({ x: w * 0.08, y: 45, type: 'star', color: '#fcd34d' });
  result.push({ x: w * 0.55, y: 25, type: 'star', color: '#f59e0b' });

  return result;
});

onMounted(() => {
  updateDimensions();
  window.addEventListener('resize', updateDimensions);

  // Animate completed road
  nextTick(() => {
    animatedOffset.value = completedPathLength.value;
    requestAnimationFrame(() => {
      animatedOffset.value = '0';
    });
  });

  // Auto-scroll to current lesson
  nextTick(() => {
    setTimeout(() => {
      const currentEl = roadContainer.value?.querySelector('[data-lesson-status="current"]');
      if (currentEl) {
        currentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateDimensions);
});
</script>

<style scoped>
.road-map-container {
  max-height: calc(100vh - 140px);
  scrollbar-width: thin;
}

.road-map-container::-webkit-scrollbar {
  width: 6px;
}

.road-map-container::-webkit-scrollbar-thumb {
  background: #86efac;
  border-radius: 3px;
}

.road-bg {
  background: linear-gradient(
    to bottom,
    #bbf7d0 0%,
    #dcfce7 30%,
    #d9f99d 60%,
    #ecfccb 80%,
    #bbf7d0 100%
  );
}

.road-completed-line {
  transition: stroke-dashoffset 1.5s ease-out;
}
</style>
